import React, { useMemo, useState } from 'react';
import { Prompt } from 'react-router-dom';
import type { FormikProps } from 'formik';
import { Formik, useFormikContext } from 'formik';

import useClaimFieldsConfiguration from '~/components/ClaimPage/hooks/useClaimFieldsConfiguration';
import { CLAIM_FIELD_CONFIG_TYPE } from '~/components/ClaimPage/types';
import { ConfirmModal } from '~/components/ConfirmModal';
import { Text } from '~/components/core';
import AlertBanner from '~/components/core/AlertBanner';
import Breadcrumbs from '~/components/core/Atomic/Breadcrumbs';
import Button from '~/components/core/Atomic/Buttons/Button';
import { FeedbackSnackbar, useSnackbar } from '~/components/core/Molecules/Snackbar';
import SkeletonTable from '~/components/core/Skeletons/SkeletonTable';
import useUnloadWindowEventListener from '~/components/hooks/useUnloadWindowEventListener';
import useDataFetcher from '~/components/useDataFetcher';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { ClaimFieldsFormDetailResponse } from '~/server_shared/openapi/data-contracts';
import cn from '~/Utils/cn';

import {
  getForm,
  getFormUrl,
  getStoredConfiguration,
  isLostResponse,
  resetForm,
  saveClaimBarConfiguration,
} from '../apiService';
import { getCatalogNamespace } from '../claimBarFieldKey';
import ClaimBarFieldList, { useDisplayedClaimBarFields } from '../ClaimBarFieldList';
import type { ClaimBarFieldEntry } from '../types';

import AddFieldButton from './AddFieldButton';
import ClaimBarConfigurationsFooter from './ClaimBarConfigurationsFooter';
import type { HeldClaimBar } from './heldEdits';
import {
  applyHeldEdits,
  blankNamedFields,
  buildSaveBody,
  isHeldStored,
  NO_HELD_EDITS,
  orderAfterDrag,
  withFieldEdit,
  withOrder,
} from './heldEdits';
import ScopeBanner from './ScopeBanner';
import type { ClaimBarForm, ConfigurationScope } from './scopes';
import { CONFIGURATIONS_TEXTS as TEXTS } from './texts';
import type { ClaimFieldCatalogFetch } from './useClaimFieldCatalog';
import useClaimFieldCatalog from './useClaimFieldCatalog';

type ConfigurationPaneProps = {
  organizationId: number;
  scope: ConfigurationScope;
  /** The configuration's scope, broadest first: the LOB, then the sub-organization. */
  crumbs: string[];
  defaultForm: ClaimBarForm | undefined;
  reloadForms: () => Promise<unknown>;
  /** The pane's held changes, read by the screen when an administrator switches scope. */
  formRef: React.Ref<FormikProps<HeldClaimBar>>;
  /** The height the pane had before the scope changed, held while the new configuration loads. */
  loadingMinHeight?: number;
  hasSubOrganizations: boolean;
};

/**
 * A configuration as a stored Save left it, built from what the pane showed and the held changes, for when reading
 * that Save back fails. Who saved it is unread, so it carries no "Last updated".
 */
const withSavedEdits = (detail: ClaimFieldsFormDetailResponse, held: HeldClaimBar): ClaimFieldsFormDetailResponse => {
  const storedOwn = new Map(detail.claim_bar.map((entry) => [entry.field_key, entry.is_own]));
  const changed = new Set([...Object.keys(held.fields), ...held.added.map((added) => added.field_key)]);
  return {
    ...detail,
    claim_bar: applyHeldEdits(detail.claim_bar, held).map((entry) => ({
      ...entry,
      is_own: changed.has(entry.field_key) || (storedOwn.get(entry.field_key) ?? false),
    })),
    last_updated_datetime: null,
    last_updated_by_username: null,
  };
};

/** The browser's own "Leave site?" box, for closing or reloading the tab while it is mounted. */
const LeaveSiteWarning = () => {
  useUnloadWindowEventListener();
  return null;
};

const ConfigurationPane = (props: ConfigurationPaneProps) => {
  const { organizationId, scope, defaultForm, reloadForms, formRef } = props;
  const { form } = scope;
  // A scope without a configuration renders the Default, so that is what its pane shows.
  const detailFormId = form?.id ?? defaultForm?.id;
  // What a save stored, read back from the configuration it names. The pane shows it once the reread list names
  // that configuration too, so it is never fetched a second time.
  const [savedDetail, setSavedDetail] = useState<ClaimFieldsFormDetailResponse | null>(null);
  const isSavedDetailShown = savedDetail !== null && savedDetail.id === detailFormId;
  // No url while the saved detail is shown: the fetcher resumes from the next configuration the pane shows, never
  // from one a reset has since deleted.
  const fetchedFormId = detailFormId === undefined || isSavedDetailShown ? undefined : detailFormId;
  const {
    data: fetchedDetail,
    isError: isDetailError,
    reloadData: reloadFetchedDetail,
  } = useDataFetcher<ClaimFieldsFormDetailResponse>(
    fetchedFormId === undefined ? null : getFormUrl(organizationId, fetchedFormId),
    null,
    fetchedFormId !== undefined
  );
  const detail = isSavedDetailShown ? savedDetail : fetchedDetail;
  // An organization that never edited its claim fields has no Default row, so no detail to read.
  const legacy = useClaimFieldsConfiguration(CLAIM_FIELD_CONFIG_TYPE.CLAIM_BAR);
  const { snackbarState, showSnackbar, closeSnackbar } = useSnackbar();
  // The pane remounts per scope. Only a pane that mounted with a configuration to read waits for it behind a skeleton;
  // a configuration that appears later in the same pane (a first edit creating it) is read with the list kept on screen.
  const [hadFormAtMount] = useState(() => detailFormId !== undefined);
  // A scope's configuration shows only the configured fields its FNOL Configuration includes, which its catalog
  // lists; the Default shows them all, and reads the catalog only when Add Field opens.
  const isDefault = scope.lob === null;
  // Read where it can filter what an administrator edits: a scope the organization still offers and the caller may
  // edit. The server refuses a catalog elsewhere (a LOB no longer supported), and there the list hides nothing.
  const isCatalogRead = !isDefault && scope.isOffered && (form ? form.is_editable : true);
  const catalog = useClaimFieldCatalog(organizationId, scope.lob, scope.subOrganizationId, isCatalogRead);
  const isCatalogLoading = isCatalogRead && catalog.data === undefined && !catalog.isError;
  const isDetailLoading = (hadFormAtMount && detail === undefined) || isCatalogLoading;
  // Until the selected configuration's own detail is read, what the list shows stands in for it (the Default's detail,
  // or the stored list) and is read-only, so no edit is computed from it.
  const isStandIn = detailFormId !== undefined && detail?.id !== detailFormId && !isDetailLoading;

  // Until a configuration that appeared in this pane is read, the stored list it showed before stays on screen.
  const entries: ClaimBarFieldEntry[] = useMemo(
    () => (detailFormId === undefined ? legacy.fieldsConfiguration : (detail?.claim_bar ?? legacy.fieldsConfiguration)),
    [detailFormId, legacy.fieldsConfiguration, detail]
  );

  // A reset deletes the configuration, so its detail is gone: the reread list drops it, and the pane then reads
  // the Default's detail instead.
  const reloadAfterReset = () => Promise.all([reloadForms(), legacy.reloadData()]);

  if (isDetailError || (detailFormId === undefined && legacy.isError)) {
    return <SkeletonTable rowsCount={8} columnsCount={1} isError />;
  }

  return (
    <Formik<HeldClaimBar>
      initialValues={NO_HELD_EDITS}
      innerRef={formRef}
      // The row shows which name is blank; Save lists the fields in the toast and sends nothing.
      validate={(held) => {
        const blank = blankNamedFields(held, entries);
        return blank.length ? { blankNames: blank.map(() => TEXTS.nameRequiredOnSave) } : {};
      }}
      onSubmit={async (values, { resetForm: resetHeld }) => {
        // The rail's count, the configuration a new scope now has, and the claim screens' Default.
        const finishSave = async () => {
          await Promise.all([reloadForms(), legacy.reloadData()]);
          resetHeld();
        };
        let saved;
        try {
          saved = await saveClaimBarConfiguration(organizationId, buildSaveBody(scope, values));
        } catch (error) {
          if (!isLostResponse(error)) {
            // The save already reported its error and wrote nothing, so the held changes stay for another try.
            return;
          }
          // No answer came, so the save may or may not have been stored: what the scope now stores tells.
          const stored = await getStoredConfiguration(organizationId, scope).catch(() => null);
          if (stored === null || !isHeldStored(values, stored.claim_bar)) {
            showSnackbar(TEXTS.saveConnectionLost, 'error');
            return;
          }
          setSavedDetail(stored);
          await finishSave();
          showSnackbar(TEXTS.savedAfterLostResponse, 'success');
          return;
        }
        // Read back before the held changes are dropped, so the screen shows one or the other throughout.
        if (saved.form_id !== null) {
          try {
            setSavedDetail(await getForm(organizationId, saved.form_id));
          } catch {
            // Reported. The server stored the save, so a configuration the pane already shows keeps the held changes
            // on screen, never an older copy. A configuration this save created is read once the reread list names it.
            if (detail?.id === saved.form_id) {
              setSavedDetail(withSavedEdits(detail, values));
            } else {
              setSavedDetail(null);
              await reloadFetchedDetail();
            }
          }
        }
        await finishSave();
      }}
    >
      <>
        <ConfigurationPaneBody
          {...props}
          entries={entries}
          detail={detail}
          isDetailLoading={isDetailLoading}
          isStandIn={isStandIn}
          reloadAfterReset={reloadAfterReset}
          catalog={isCatalogRead ? catalog : undefined}
        />
        <FeedbackSnackbar state={snackbarState} onClose={closeSnackbar} />
      </>
    </Formik>
  );
};

type ConfigurationPaneBodyProps = ConfigurationPaneProps & {
  entries: ClaimBarFieldEntry[];
  detail: ClaimFieldsFormDetailResponse | undefined;
  isDetailLoading: boolean;
  isStandIn: boolean;
  reloadAfterReset: () => Promise<unknown>;
  /**
   * The scope's catalog; absent where the pane reads none: the Default, a scope no longer offered, or one the
   * caller cannot edit.
   */
  catalog: ClaimFieldCatalogFetch | undefined;
};

const ConfigurationPaneBody = ({
  organizationId,
  scope,
  crumbs,
  entries,
  detail,
  isDetailLoading,
  isStandIn,
  loadingMinHeight,
  reloadAfterReset,
  catalog,
  hasSubOrganizations,
}: ConfigurationPaneBodyProps) => {
  const {
    values,
    setValues,
    dirty,
    isSubmitting,
    handleSubmit,
    resetForm: discardHeld,
  } = useFormikContext<HeldClaimBar>();
  const [isResetting, setIsResetting] = useState(false);
  const [isResetConfirmOpen, setIsResetConfirmOpen] = useState(false);
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const isDefault = scope.lob === null;
  const { form } = scope;
  const merged = useMemo(() => applyHeldEdits(entries, values), [entries, values]);
  // A field no claim of this scope can show is left out of view and of the count, but stays held, in its stored slot
  // of the order a Save sends, so it returns as configured once it can show again: a configured field the scope's
  // catalog does not list (inactive in, or missing from, its FNOL Configuration; an unread catalog hides nothing), and
  // Sub Organization in an organization without sub-organizations. The Default lists every field.
  const offeredKeys = useMemo(
    () => (catalog?.data ? new Set(catalog.data.fields.map((field) => field.field_key)) : null),
    [catalog?.data]
  );
  const shownByOrganization = useDisplayedClaimBarFields(merged);
  const displayedFields = useMemo(
    () =>
      isDefault
        ? shownByOrganization
        : shownByOrganization.filter(
            (field) =>
              (hasSubOrganizations || field.field_key !== 'sub_organization') &&
              (offeredKeys === null ||
                getCatalogNamespace(field.field_key) !== 'configured_fields' ||
                offeredKeys.has(field.field_key))
          ),
    [isDefault, shownByOrganization, hasSubOrganizations, offeredKeys]
  );
  const resolvedKeys = useMemo(() => new Set(merged.map((entry) => entry.field_key)), [merged]);
  const shownCount = displayedFields.filter((field) => field.is_enabled).length;

  const canEdit = !!form?.is_editable;
  // The server's save refuses a LOB the scope no longer supports, so a scope without a configuration is editable
  // only while it is offered.
  const isEditableHere = isDefault || (form ? form.is_editable : scope.isOffered);
  const isBusy = isSubmitting || isResetting;

  const storedEntry = (fieldKey: string) => entries.find((entry) => entry.field_key === fieldKey);

  const reorder = ({ fromIndex, toIndex }: { fromIndex: number; toIndex: number }) => {
    const displayedKeys = displayedFields.map((field) => field.field_key);
    const reordered = [...displayedKeys];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);
    setValues(
      withOrder(
        values,
        entries,
        orderAfterDrag(
          merged.map((entry) => entry.field_key),
          new Set(displayedKeys),
          reordered
        )
      )
    );
  };

  const lastUpdated =
    !isStandIn && detail?.last_updated_datetime
      ? TEXTS.lastUpdated(serverDateTimeToLocal(detail.last_updated_datetime), detail.last_updated_by_username ?? null)
      : null;

  return (
    // The bottom padding keeps the last row clear of the fixed footer.
    <div
      className="pb-96 flex flex-col gap-16 ps-4"
      style={isDetailLoading ? { minHeight: loadingMinHeight } : undefined}
    >
      <Prompt when={dirty} message={TEXTS.leaveWithoutSaving} />
      {dirty ? <LeaveSiteWarning /> : null}
      <div className="flex flex-wrap items-center justify-between gap-12">
        <Breadcrumbs aria-label="breadcrumb">
          {crumbs.map((crumb, index) => (
            <Text
              key={crumb}
              variant={Text.VARIANTS.LG}
              weight={index === crumbs.length - 1 ? Text.WEIGHTS.SEMI_BOLD : Text.WEIGHTS.REGULAR}
              colorVariant={index === crumbs.length - 1 ? Text.COLOR_VARIANTS.PRIMARY : Text.COLOR_VARIANTS.SECONDARY}
            >
              {crumb}
            </Text>
          ))}
        </Breadcrumbs>
      </div>

      {form && !isDefault ? (
        <ScopeBanner
          label={TEXTS.custom}
          scope="custom"
          lastUpdated={lastUpdated}
          action={
            canEdit ? (
              <Button
                color="primary"
                variant="outlined"
                className="shrink-0 whitespace-nowrap"
                disabled={isBusy || isDetailLoading || isStandIn}
                onClick={() => setIsResetConfirmOpen(true)}
              >
                {TEXTS.resetToDefault}
              </Button>
            ) : null
          }
        />
      ) : null}
      {isDefault ? <ScopeBanner label={TEXTS.default} scope="default" lastUpdated={form ? lastUpdated : null} /> : null}

      {!isDefault && (form || isEditableHere) ? (
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.INFO}
          note={
            <>
              <span>{form ? TEXTS.customFollowsDefault : TEXTS.usesDefault}</span>
              {catalog !== undefined ? (
                <>
                  <br />
                  <span>{TEXTS.usesDefaultFnolNote}</span>
                </>
              ) : null}
            </>
          }
          withIcon
        />
      ) : null}
      {!isEditableHere ? (
        <AlertBanner alertType={AlertBanner.ALERT_TYPES.INFO} note={TEXTS.readOnlyNotice} withIcon />
      ) : null}
      {isDefault ? (
        <AlertBanner
          alertType={AlertBanner.ALERT_TYPES.INFO}
          note={
            <>
              <span>{TEXTS.defaultPropagation}</span>
              <br />
              <span>{TEXTS.defaultFnolNote}</span>
            </>
          }
          withIcon
        />
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-12">
        <div className="flex items-baseline gap-12">
          <Text weight={Text.WEIGHTS.SEMI_BOLD}>{TEXTS.fieldsHeading}</Text>
          {/* Kept in place while unread, so the heading does not move when the list arrives. */}
          <Text
            variant={Text.VARIANTS.SM}
            colorVariant={Text.COLOR_VARIANTS.SECONDARY}
            className={cn({ invisible: isDetailLoading || isStandIn })}
          >
            {TEXTS.fieldsCount(shownCount, displayedFields.length - shownCount)}
          </Text>
        </div>
        {isEditableHere && (isDefault || scope.isOffered) ? (
          <div className={cn({ invisible: isDetailLoading })}>
            <AddFieldButton
              organizationId={organizationId}
              lob={scope.lob}
              subOrganizationId={scope.subOrganizationId}
              resolvedKeys={resolvedKeys}
              catalog={catalog}
              disabled={isBusy || isStandIn}
              onAdd={(field) => setValues({ ...values, added: [...values.added, { ...field, is_enabled: true }] })}
            />
          </div>
        ) : null}
      </div>

      {isDetailLoading ? (
        <SkeletonTable rowsCount={8} columnsCount={1} />
      ) : (
        <ClaimBarFieldList
          displayedFields={displayedFields}
          onToggleEnabled={(fieldKey, isEnabled) =>
            setValues(withFieldEdit(values, fieldKey, { is_enabled: isEnabled }, storedEntry(fieldKey)))
          }
          onDisplayNameChange={(fieldKey, displayName) =>
            setValues(withFieldEdit(values, fieldKey, { display_name: displayName }, storedEntry(fieldKey)))
          }
          onReorder={reorder}
          disabled={isBusy || !isEditableHere || isStandIn}
          dimHiddenFields
          holdTypedNames
          fitTitlesToControls
        />
      )}

      {isEditableHere ? (
        <ClaimBarConfigurationsFooter
          onCancel={() => (dirty ? setIsDiscardConfirmOpen(true) : undefined)}
          onSave={() => handleSubmit()}
          isCancelDisabled={isBusy}
          isSaveDisabled={isBusy || !dirty}
          resolveErrorPath={(_item, index) => [
            blankNamedFields(values, entries, catalog?.data?.fields)[index]?.label ?? '',
          ]}
        />
      ) : null}

      <ConfirmModal
        isOpen={isDiscardConfirmOpen}
        title={TEXTS.discardTitle}
        contentText={TEXTS.discardText}
        primaryButtonName={TEXTS.discardConfirm}
        onClose={() => setIsDiscardConfirmOpen(false)}
        onPrimaryBtnClick={() => {
          discardHeld();
          setIsDiscardConfirmOpen(false);
        }}
      />
      <ConfirmModal
        isOpen={isResetConfirmOpen}
        isSubmitting={isResetting}
        title={TEXTS.resetConfirmTitle}
        contentText={TEXTS.resetConfirmText}
        primaryButtonName={TEXTS.resetToDefault}
        onClose={() => setIsResetConfirmOpen(false)}
        onPrimaryBtnClick={async () => {
          if (form) {
            setIsResetting(true);
            try {
              await resetForm(organizationId, form.id);
              discardHeld();
              await reloadAfterReset();
            } catch {
              // The reset already reported its error; nothing changed.
            } finally {
              setIsResetting(false);
            }
          }
          setIsResetConfirmOpen(false);
        }}
      />
    </div>
  );
};

export default ConfigurationPane;
